import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    loadCandidateLiaisonNameByContractId,
    loadCandidateLiaisonNameByContractIdSuccess,
    loadDocumentDetailsFailure,
    loadDocumentDetailsSuccess,
    loadDocumentIds,
    loadDocumentIdsFailure,
    loadDocumentIdsSuccess,
    selectedDocumentId
} from 'src/app/compliance/store/actions/document-reviewer.actions';
import {
    selectComplianceAssignedUploaderId,
    selectComplianceCandidateId,
    selectComplianceIsCandidateUpload,
    selectComplianceIsSignedContract
} from 'src/app/compliance/store/selectors/compliance.selectors';
import { CandidateDocumentDetail } from '../../models/candidate-document-detail.model';
import { DocumentUploadToolClient } from '../../api/document-upload-tool-client.service';
import { CandidateDocumentViewerDto } from '../../api/models/candidate-document-viewer-dto.model';

@Injectable()
export class DocumentDetailsEffect {
    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store,
        private readonly _documentUploadService: DocumentUploadToolClient
    ) {}

    getCandidateLiaison$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadCandidateLiaisonNameByContractId),
            switchMap((props) => {
                if (props.contractId) {
                    return this._documentUploadService.candidateLiaisonByContractId(props.contractId).pipe(
                        map((cl) => loadCandidateLiaisonNameByContractIdSuccess({ candidateLiaison: cl })),
                        catchError((result) => of(loadDocumentDetailsFailure({ error: result })))
                    );
                } else {
                    return of(loadDocumentDetailsFailure({ error: 'Failed Candidate Liaison Details' }));
                }
            })
        )
    );

    getDocumentDetails$ = createEffect(() =>
        this._actions$.pipe(
            ofType(selectedDocumentId),
            switchMap((selected) => {
                if (selected.documentId) {
                    return this._documentUploadService.getCandidateDocumentDetail(selected.documentId).pipe(
                        map((dto: CandidateDocumentViewerDto) =>
                            loadDocumentDetailsSuccess({
                                details: dto as unknown as CandidateDocumentDetail
                            })
                        ),
                        catchError((result) => of(loadDocumentDetailsFailure({ error: result })))
                    );
                } else {
                    return of(loadDocumentDetailsFailure({ error: 'Unknown document id ' }));
                }
            })
        )
    );

    getCandidateDocumentIds$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadDocumentIds),
            concatLatestFrom(() => [
                this._store.select(selectComplianceCandidateId),
                this._store.select(selectComplianceAssignedUploaderId),
                this._store.select(selectComplianceIsSignedContract),
                this._store.select(selectComplianceIsCandidateUpload)
            ]),
            switchMap(([action, candidateId, assignedUploaderId, isSignedContract, isCandidateUpload]) => {
                return this._documentUploadService
                    .getCandidateDocumentIds(candidateId, assignedUploaderId, isSignedContract, isCandidateUpload)
                    .pipe(
                        map((documentIds) => loadDocumentIdsSuccess({ documentIds: documentIds })),
                        catchError((result) => of(loadDocumentIdsFailure({ error: result })))
                    );
            })
        )
    );
}
